'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import type { LearningXp } from '@/app/track/utils/xpTypes';
import { EMPTY_XP } from '@/app/track/utils/xpTypes';
import { fetchLearningXp, XP_UPDATED_EVENT } from '@/utils/learningXp';

export function useLearningXp() {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const accessToken = (session?.user as { accessToken?: string } | undefined)?.accessToken;
  const [xp, setXp] = useState<LearningXp>(EMPTY_XP);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    if (!accessToken) {
      setXp(EMPTY_XP);
      setLoading(false);
      return;
    }

    try {
      const data = await fetchLearningXp(accessToken);
      setXp(data);
    } catch {
      setXp(EMPTY_XP);
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (status === 'loading') return;
    void reload();
  }, [status, reload, pathname]);

  useEffect(() => {
    const onXpUpdated = (event: Event) => {
      const detail = (event as CustomEvent<LearningXp>).detail;
      if (detail) {
        setXp(detail);
        setLoading(false);
      }
    };

    window.addEventListener(XP_UPDATED_EVENT, onXpUpdated);
    return () => window.removeEventListener(XP_UPDATED_EVENT, onXpUpdated);
  }, []);

  return { xp, loading, reload };
}
